import { useState } from 'react';
import { KeyRound, PenLine } from 'lucide-react';
import { Button, Pill } from '@/shared/components';
import { formatDateTime } from '@/shared/lib/format';
import type { ActivityEntry } from './userActivity';

/** Ein Punkt der Zeitleiste. Der Vorher/Nachher-Block klappt auf, statt die Leiste zu fluten. */
export function UserActivityRow({ entry }: { entry: ActivityEntry }) {
  const [open, setOpen] = useState(false);
  const detailId = `activity-diff-${entry.id}`;
  const Icon = entry.kind === 'account' ? KeyRound : PenLine;

  return (
    <li className="relative">
      <span
        className="border-line bg-card absolute -left-[25px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full border"
        aria-hidden
      >
        <Icon size={9} className="text-ink-faint" />
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="text-ink wrap-break-word text-sm font-medium">{entry.title}</p>
        {entry.area && <span className="text-ink-soft text-xs">{entry.area}</span>}
        {entry.count > 1 && (
          <Pill variant="default" size="sm" className="tabular-nums">
            {entry.count}×
          </Pill>
        )}
        {entry.rawAction && (
          // Der Rohwert bleibt daneben stehen, damit sich die Zeile mit dem Änderungsprotokoll
          // abgleichen lässt — dort wird nach genau dieser Zeichenkette gefiltert. `ink-faint`
          // ergäbe auf Card rund 2,4:1; für etwas, das man ablesen soll, zu wenig (§12).
          <span className="text-ink-soft max-w-56 truncate font-mono text-[11px]">
            {entry.rawAction}
          </span>
        )}
      </div>
      <p className="text-ink-faint mt-0.5 text-xs tabular-nums">
        {entry.since
          ? `${formatDateTime(entry.since)} – ${formatDateTime(entry.at)}`
          : formatDateTime(entry.at)}
        {entry.detail && <span className="ml-2 font-mono">{entry.detail}</span>}
      </p>
      {entry.diff && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 min-h-11"
            aria-expanded={open}
            aria-controls={open ? detailId : undefined}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? 'Änderung zuklappen' : 'Änderung ansehen'}
          </Button>
          {open && (
            <pre
              id={detailId}
              className="bg-card-alt text-ink mt-1 overflow-x-auto whitespace-pre-wrap rounded-md p-3 font-mono text-xs"
            >
              {tryFormatJson(entry.diff)}
            </pre>
          )}
        </>
      )}
    </li>
  );
}

function tryFormatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
